import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  FieldHint,
  Label,
  RadioGroup,
  RadioGroupItem,
  Switch,
  useToast,
} from "@repo/ui";
import { AlertTriangle, Loader2, Settings } from "lucide-react";
import {
  getPharmacyOptions,
  updatePharmacyOptions,
  type PharmacyOptions,
} from "@/lib/api/pharmacy";

export function GeneralOptionsSection() {
  const { toast } = useToast();
  const [options, setOptions] = useState<PharmacyOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [confirmStockOnDispense, setConfirmStockOnDispense] = useState(false);
  const [confirmStockScope, setConfirmStockScope] = useState<
    "cii_only" | "all"
  >("cii_only");
  const [requirePatientOnDispense, setRequirePatientOnDispense] =
    useState(false);

  const fetchOptions = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const response = await getPharmacyOptions();
      setOptions(response.data);
      setConfirmStockOnDispense(response.data.confirm_stock_on_dispense);
      setConfirmStockScope(response.data.confirm_stock_scope);
      setRequirePatientOnDispense(response.data.require_patient_on_dispense);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const hasChanges =
    options !== null &&
    (confirmStockOnDispense !== options.confirm_stock_on_dispense ||
      confirmStockScope !== options.confirm_stock_scope ||
      requirePatientOnDispense !== options.require_patient_on_dispense);

  async function handleSave() {
    setIsSaving(true);
    try {
      await updatePharmacyOptions({
        confirm_stock_on_dispense: confirmStockOnDispense,
        confirm_stock_scope: confirmStockScope,
        require_patient_on_dispense: requirePatientOnDispense,
      });
      setOptions((prev) =>
        prev
          ? {
              ...prev,
              confirm_stock_on_dispense: confirmStockOnDispense,
              confirm_stock_scope: confirmStockScope,
              require_patient_on_dispense: requirePatientOnDispense,
            }
          : prev
      );
      toast({
        title: "Options updated",
        description: "Pharmacy options have been saved.",
        variant: "success",
      });
    } catch {
      toast({
        title: "Failed to save",
        description: "Could not update pharmacy options. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-3 py-12">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">
            Failed to load pharmacy options.
          </p>
          <Button variant="outline" size="sm" onClick={fetchOptions}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          <CardTitle>Workflow Options</CardTitle>
        </div>
        <CardDescription>
          Configure pharmacy workflow settings. These options affect all staff
          members.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor="confirm-stock-toggle"
                  className="text-sm font-medium cursor-pointer"
                >
                  Confirm stock on dispense
                </Label>
                <FieldHint>
                  Adds a manual count checkpoint to the audit trail. Catches
                  count errors at the moment they happen instead of during a
                  later audit.
                </FieldHint>
              </div>
              <p className="text-sm text-muted-foreground">
                Require staff to verify physical stock count before completing a
                dispense. Creates an audit record for each verification.
              </p>
            </div>
            <Switch
              id="confirm-stock-toggle"
              checked={confirmStockOnDispense}
              onCheckedChange={setConfirmStockOnDispense}
            />
          </div>

          {confirmStockOnDispense && (
            <div className="ml-1 space-y-2">
              <div className="flex items-center gap-1.5">
                <Label className="text-sm font-medium text-foreground">
                  Apply to
                </Label>
                <FieldHint>
                  CII only is the minimum DEA scope. Pick All controlled
                  substances to extend the verification step to CIII, CIV, and
                  CV as well.
                </FieldHint>
              </div>
              <RadioGroup
                value={confirmStockScope}
                onValueChange={(value: string) =>
                  setConfirmStockScope(value as "cii_only" | "all")
                }
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="cii_only" id="scope-cii-only" />
                  <Label
                    htmlFor="scope-cii-only"
                    className="text-sm font-normal cursor-pointer"
                  >
                    CII only
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="all" id="scope-all" />
                  <Label
                    htmlFor="scope-all"
                    className="text-sm font-normal cursor-pointer"
                  >
                    All controlled substances
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label
                htmlFor="require-patient-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                Require patient on dispense
              </Label>
              <FieldHint>
                When off, staff can save a dispense without linking a patient.
                Faster for staff but leaves gaps in your dispensing history
                during an audit.
              </FieldHint>
            </div>
            <p className="text-sm text-muted-foreground">
              Require staff to select a patient before completing a dispense.
              When disabled, the patient field is optional.
            </p>
          </div>
          <Switch
            id="require-patient-toggle"
            checked={requirePatientOnDispense}
            onCheckedChange={setRequirePatientOnDispense}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
